import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}

export default function Register() {
  const { register, status } = useAuth();
  const t = useTranslation();
  const [form, setForm] = useState({ email: "", password: "", organizationName: "", organizationSlug: "" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (status === "authenticated") return <Navigate to="/sensors" replace />;

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "organizationName" && !f.organizationSlug) next.organizationSlug = slugify(v);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        organizationName: form.organizationName.trim(),
        organizationSlug: slugify(form.organizationSlug || form.organizationName),
      });
      toast.success(t("common.toasts.organizationCreated"));
      navigate("/sensors", { replace: true });
    } catch (err) {
      toast.error(extractApiError(err, t("auth.register.failed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6 grid-background">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl bg-card border border-border p-8 space-y-5" data-testid="register-form">
        <div>
          <div className="mono-eyebrow mb-2">{t("auth.register.eyebrow")}</div>
          <h2 className="font-display text-2xl tracking-tight">{t("auth.register.title")}</h2>
          <p className="text-xs text-muted-foreground mt-1">{t("auth.register.description")}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">{t("auth.register.organizationName")}</Label>
          <Input value={form.organizationName} onChange={(e) => update("organizationName", e.target.value)} required data-testid="register-org-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">
            {t("auth.register.slug")} <span className="text-muted-foreground font-mono">{t("auth.register.slugHint")}</span>
          </Label>
          <Input value={form.organizationSlug} onChange={(e) => update("organizationSlug", e.target.value)} required pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]|[a-z0-9]" minLength={2} maxLength={60} data-testid="register-org-slug-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">{t("common.fields.email")}</Label>
          <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="register-email-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">
            {t("common.fields.password")} <span className="text-muted-foreground font-mono">{t("common.misc.minEightChars")}</span>
          </Label>
          <Input type="password" value={form.password} onChange={(e) => update("password", e.target.value)} required minLength={8} data-testid="register-password-input" />
        </div>
        <Button type="submit" className="w-full rounded-lg" disabled={busy} data-testid="register-submit-button">
          {busy ? t("auth.register.submitting") : t("auth.register.submit")}
        </Button>
        <div className="text-xs text-muted-foreground text-center">
          {t("auth.register.alreadyRegistered")}{" "}
          <Link to="/login" className="text-foreground hover:underline" data-testid="register-login-link">
            {t("auth.register.signIn")}
          </Link>
        </div>
      </form>
    </div>
  );
}
